<template>
  <DxPopup
    width="100vw"
    height="100vh"
    :show-close-button="false"
    :visible="visible"
    :resize-enabled="false"
    :drag-enabled="false"
    :wrapper-attr="popupAttributes"
    @hidden="onHidden"
  >
    <template #title>
      <div class="machine-split-header">
        <div class="machine-split-back" @click="onCancel">
          <img src="@/assets/images/icons/arrow-back.svg" />
          <span>{{ $t("common.back") }}</span>
        </div>
        <div class="machine-split-title">{{ $t("stock_mobile.form.split_quantity_by_machine_and_parcel") }}</div>
      </div>
    </template>
    <template #content>
      <div class="machine-split-content">
        <DxScrollView class="machine-split-scrollview" :use-native="true">
          <div class="machine-split-products">
            <div v-for="(product, pIdx) in productRows" :key="product.product_uuid" class="machine-split-product">
              <div class="machine-split-product-header">
                <span class="machine-split-product-name">{{ product.product_name }}</span>
                <span class="machine-split-product-total">{{ formatQty(product.total_quantity) }} {{ product.measure_unit_symbol }}</span>
              </div>
              <div
                class="machine-split-sum-indicator"
                :class="{ 'machine-split-sum-ok': isProductSumOk(product), 'machine-split-sum-error': !isProductSumOk(product) }"
              >
                {{ $t("stock_mobile.form.distributed") }}: {{ formatQty(productDistributed(product)) }} {{ product.measure_unit_symbol }}
              </div>

              <div
                v-for="(machineRow, mIdx) in product.machine_rows"
                :key="machineRow.work_machine_uuid"
                class="machine-split-machine-group"
                :class="{ 'machine-split-machine-group-separated': mIdx > 0 }"
              >
                <div class="machine-split-machine-row">
                  <span class="machine-split-machine-name">{{ machineRow.machine_name }}</span>
                  <DxNumberBox
                    :value="machineRow.quantity"
                    :min="0"
                    :width="90"
                    :show-spin-buttons="false"
                    :show-clear-button="false"
                    :format="numberFormat"
                    styling-mode="outlined"
                    @value-changed="onMachineQtyChanged(pIdx, mIdx, $event)"
                  />
                  <span class="machine-split-machine-unit">{{ product.measure_unit_symbol }}</span>
                </div>
                <div
                  class="machine-split-sum-indicator machine-split-parcel-sum-indicator"
                  :class="{ 'machine-split-sum-ok': isMachineSumOk(machineRow), 'machine-split-sum-error': !isMachineSumOk(machineRow) }"
                >
                  {{ $t("stock_mobile.form.distributed") }}: {{ formatQty(machineDistributed(machineRow)) }} {{ product.measure_unit_symbol }}
                </div>
                <div
                  v-for="(parcelRow, plIdx) in machineRow.parcel_rows"
                  :key="parcelRow.parcel_uuid"
                  class="machine-split-parcel-row"
                >
                  <span class="machine-split-parcel-name">{{ parcelRow.parcel_name }}</span>
                  <DxNumberBox
                    :value="parcelRow.quantity"
                    :min="0"
                    :width="90"
                    :show-spin-buttons="false"
                    :show-clear-button="false"
                    :format="numberFormat"
                    styling-mode="outlined"
                    @value-changed="onParcelQtyChanged(pIdx, mIdx, plIdx, $event)"
                  />
                  <span class="machine-split-machine-unit">{{ product.measure_unit_symbol }}</span>
                </div>
              </div>
              <div class="dialog_title_separator"></div>
            </div>
          </div>
          <div class="machine-split-bottom-spacer"></div>
        </DxScrollView>
        <div v-if="globalError" class="machine-split-global-error">{{ globalError }}</div>
        <div class="machine-split-actions">
          <div class="sticky_action_button" :class="{ 'machine-split-btn-disabled': !allSumsOk }" @click="onConfirm">
            {{ $t("stock_mobile.form.confirm_and_save") }}
          </div>
        </div>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { DxScrollView } from "devextreme-vue/scroll-view";
import { DxNumberBox } from "devextreme-vue/number-box";
import { mapGetters } from "vuex";
import { getQuantityFormatter } from "@/helpers/stockHelper";

export default {
  name: "MachineParcelSplitPopup",
  components: {
    DxPopup,
    DxScrollView,
    DxNumberBox,
  },
  data() {
    return {
      visible: false,
      productRows: [],
      globalError: null,
      promiseResolve: null,
      popupAttributes: {
        class: "product_history_dialog_wrapper machine-split-popup-wrapper",
      },
    };
  },
  computed: {
    ...mapGetters({
      quantityDecimalPlaces: "settings/getQuantityDecimalPlaces",
    }),
    numberFormat() {
      return getQuantityFormatter();
    },
    allSumsOk() {
      return this.productRows.every(
        (product) => this.isProductSumOk(product) && product.machine_rows.every((machineRow) => this.isMachineSumOk(machineRow)),
      );
    },
  },
  methods: {
    /**
     * Open the popup with products, machines and parcels, defaulting to an equal machine split
     * and, within each machine, a parcel split proportional to parcel surface.
     * @param {Array} products - consumData.products entries { uuid, name, quantity, measure_unit }
     * @param {Array} machines - selected machines { uuid, name }
     * @param {Array} parcels - selected parcels { parcel_uuid or uuid, name, surface }
     * @returns {Promise<Array|null>} resolves with
     *   [{ product_uuid, machine_splits: [{ work_machine_uuid, quantity, parcel_splits: [{ parcel_uuid, quantity }] }] }] or null on cancel
     */
    open(products = [], machines = [], parcels = []) {
      return new Promise((resolve) => {
        const normalizedParcels = parcels.map((p) => ({
          parcel_uuid: p.parcel_uuid || p.uuid,
          name: p.name,
          surface: parseFloat(p.surface) || 0,
        }));
        this.productRows = products.map((product) => this.buildProductRow(product, machines, normalizedParcels));
        this.globalError = null;
        this.promiseResolve = resolve;
        this.visible = true;
      });
    },
    buildProductRow(product, machines, parcels) {
      const totalQuantity = parseFloat(product.quantity) || 0;
      const equalShare = machines.length > 0 ? this.roundQty(totalQuantity / machines.length) : 0;

      let dividedQuantity = 0;
      const machine_rows = machines.map((machine, idx) => {
        const isLast = idx === machines.length - 1;
        const quantity = isLast ? this.roundQty(totalQuantity - dividedQuantity) : equalShare;
        if (!isLast) {
          dividedQuantity += quantity;
        }
        return {
          work_machine_uuid: machine.uuid,
          machine_name: machine.name,
          quantity,
          parcel_rows: this.splitByParcelSurface(quantity, parcels),
        };
      });

      return {
        product_uuid: product.uuid,
        product_name: product.name,
        measure_unit_symbol: product.measure_unit?.symbol || product.measure_unit_symbol || "",
        total_quantity: totalQuantity,
        machine_rows,
      };
    },
    splitByParcelSurface(totalQuantity, parcels) {
      const totalSurface = parcels.reduce((sum, p) => sum + (parseFloat(p.surface) || 0), 0);

      let dividedQuantity = 0;
      return parcels.map((parcel, idx) => {
        const isLast = idx === parcels.length - 1;
        let quantity;
        if (isLast) {
          quantity = this.roundQty(totalQuantity - dividedQuantity);
        } else {
          const share = totalSurface > 0 ? parcel.surface / totalSurface : 0;
          quantity = this.roundQty(totalQuantity * share);
          dividedQuantity += quantity;
        }
        return {
          parcel_uuid: parcel.parcel_uuid,
          parcel_name: parcel.name,
          surface: parcel.surface,
          quantity,
        };
      });
    },
    roundQty(value) {
      return parseFloat((parseFloat(value) || 0).toFixed(this.quantityDecimalPlaces));
    },
    formatQty(value) {
      return parseFloat(parseFloat(value || 0).toFixed(this.quantityDecimalPlaces));
    },
    productDistributed(product) {
      return product.machine_rows.reduce((sum, row) => sum + (parseFloat(row.quantity) || 0), 0);
    },
    machineDistributed(machineRow) {
      return machineRow.parcel_rows.reduce((sum, row) => sum + (parseFloat(row.quantity) || 0), 0);
    },
    isProductSumOk(product) {
      const diff = Math.abs(
        parseFloat(this.productDistributed(product).toFixed(this.quantityDecimalPlaces)) -
          parseFloat(product.total_quantity.toFixed(this.quantityDecimalPlaces)),
      );
      return diff < Math.pow(10, -this.quantityDecimalPlaces);
    },
    isMachineSumOk(machineRow) {
      const diff = Math.abs(
        parseFloat(this.machineDistributed(machineRow).toFixed(this.quantityDecimalPlaces)) -
          parseFloat((parseFloat(machineRow.quantity) || 0).toFixed(this.quantityDecimalPlaces)),
      );
      return diff < Math.pow(10, -this.quantityDecimalPlaces);
    },
    onMachineQtyChanged(pIdx, mIdx, e) {
      const product = this.productRows[pIdx];
      const machineRow = product.machine_rows[mIdx];
      const newQuantity = parseFloat(e.value) || 0;
      // re-divide this machine's parcels proportionally by surface whenever the machine quantity is overwritten
      const parcels = machineRow.parcel_rows.map((row) => ({ parcel_uuid: row.parcel_uuid, name: row.parcel_name, surface: row.surface }));
      this.$set(product.machine_rows, mIdx, {
        ...machineRow,
        quantity: newQuantity,
        parcel_rows: this.splitByParcelSurface(newQuantity, parcels),
      });
      this.globalError = null;
    },
    onParcelQtyChanged(pIdx, mIdx, plIdx, e) {
      const product = this.productRows[pIdx];
      const machineRow = product.machine_rows[mIdx];
      const parcelRow = machineRow.parcel_rows[plIdx];
      this.$set(machineRow.parcel_rows, plIdx, {
        ...parcelRow,
        quantity: parseFloat(e.value) || 0,
      });
      this.globalError = null;
    },
    closeWith(result) {
      this.visible = false;
      if (this.promiseResolve) {
        this.promiseResolve(result);
      }
      this.promiseResolve = null;
    },
    onHidden() {
      if (this.promiseResolve) {
        this.promiseResolve(null);
        this.promiseResolve = null;
      }
      this.productRows = [];
      this.globalError = null;
    },
    onCancel() {
      this.closeWith(null);
    },
    onConfirm() {
      if (!this.allSumsOk) {
        this.globalError = this.productRows.some((product) => !this.isProductSumOk(product))
          ? this.$t("stock_mobile.form.error_machine_split_sum_must_match")
          : this.$t("stock_mobile.form.error_parcel_split_sum_must_match");
        return;
      }

      const result = this.productRows.map((product) => ({
        product_uuid: product.product_uuid,
        machine_splits: product.machine_rows.map((machineRow) => ({
          work_machine_uuid: machineRow.work_machine_uuid,
          quantity: machineRow.quantity,
          parcel_splits: machineRow.parcel_rows.map((parcelRow) => ({
            parcel_uuid: parcelRow.parcel_uuid,
            quantity: parcelRow.quantity,
          })),
        })),
      }));

      this.closeWith(result);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global/colors.scss";

.machine-split-popup-wrapper {
  :deep(.dx-popup-content) {
    padding: 0 !important;
  }
}

.machine-split-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 20px 24px 16px;

  .machine-split-back {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .machine-split-title {
    font-weight: bold;
    color: $selection-color;
  }
}

.machine-split-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.machine-split-scrollview {
  flex: 1;
}

.machine-split-products {
  padding: 12px 24px 0;
}

.machine-split-product {
  margin-bottom: 12px;
}

.machine-split-product-header {
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  margin-bottom: 4px;
}

.machine-split-sum-indicator {
  font-size: 13px;
  margin-bottom: 8px;

  &.machine-split-sum-ok {
    color: $brand;
  }

  &.machine-split-sum-error {
    color: $danger-color;
  }
}

.machine-split-machine-group {
  padding-left: 10px;
  margin-bottom: 10px;

  &.machine-split-machine-group-separated {
    border-top: 1px dotted $gray-30;
    margin-top: 14px;
    padding-top: 12px;
  }
}

.machine-split-machine-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;

  .machine-split-machine-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: bold;
  }

  .machine-split-machine-unit {
    width: 40px;
  }
}

.machine-split-parcel-sum-indicator {
  margin-left: 4px;
  margin-bottom: 6px;
}

.machine-split-parcel-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  padding-left: 10px;

  .machine-split-parcel-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .machine-split-machine-unit {
    width: 40px;
  }
}

.machine-split-global-error {
  color: $danger-color;
  padding: 0 24px 8px;
}

.machine-split-actions {
  padding: 12px 24px 24px;
}

.machine-split-btn-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.machine-split-bottom-spacer {
  height: 24px;
}
</style>
